import * as React from 'react';
import { Link } from 'react-router-dom';
import { useFormik, type FormikHelpers } from 'formik';
import * as yup from 'yup';

import { useAuth } from '../../../contexts';
import {
  TextField,
  Button,
  type AlertDetails,
  Alert,
} from '../../../components';

const PAGE_TEXT = {
  FORGOT_PASSWORD: 'Forgot password',
  EMAIL: 'Email',
  SIGNUP: 'Signup',
  VALID_EMAIL: 'Enter a valid email',
  REQUIRED_EMAIL: 'Email is required',
  BACK_TO_LOGIN: 'Back to log in page',
  SEND_RESET_EMAIL: 'Send email to reset password',
};

const validationSchema = yup.object({
  email: yup.string().required(PAGE_TEXT.REQUIRED_EMAIL),
});

type ForgotPasswordForm = {
  email: string;
};

export const ForgotPassword: React.FunctionComponent = () => {
  const initialValues = {
    email: '',
  };
  const { requestResetPassword } = useAuth();
  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertDetails, setAlertDetails] = React.useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const handleSubmit = async (
    values: ForgotPasswordForm,
    { setSubmitting }: FormikHelpers<ForgotPasswordForm>,
  ): Promise<void> => {
    const { email } = values;
    try {
      await requestResetPassword({ variables: { email } });
      setOpenAlert(true);
      setAlertDetails({
        alertContent: `An email has been sent to ${email} with further instructions.`,
        alertType: 'success',
      });
    } catch (error) {
      setOpenAlert(true);
      setAlertDetails({
        alertContent: `Request to reset the password failed, please try again later.`,
        alertType: 'error',
      });
      console.error(`Request reset password fail.`, error);
    }
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '2rem',
      }}
    >
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <h1>{PAGE_TEXT.FORGOT_PASSWORD}</h1>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        onSubmit={formik.handleSubmit}
      >
        <div style={{ padding: '5px' }}>
          <TextField
            style={{ minWidth: '350px' }}
            label={PAGE_TEXT.EMAIL}
            id="login-email"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </div>
        <div style={{ padding: '0.5rem' }}>
          <Button
            style={{ margin: '0.5rem' }}
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            {PAGE_TEXT.SEND_RESET_EMAIL}
          </Button>
        </div>
      </form>

      <Link to="/login">{PAGE_TEXT.BACK_TO_LOGIN}</Link>
    </div>
  );
};
